import { useFieldArray, UseFormReturn, Control } from "react-hook-form";
import { useSubmit } from "@/components/report/hooks";
import { Forms } from "@/types/form";
import { log } from "@/utils/logger";
import { CategoryView } from "@/types/report";
import { useMemo, useState } from "react";
import { generateRandomFilename, triggerFileDownload } from "@/utils/helpers";
import { FormField } from ".";
import useQueryAccountingCategoryAttachmentsPdf from "@/hooks/queries/useQueryAccountingCategoryAttachmentsPdf";

type ViewType = "all" | "income" | "expense";
type MakeOptional<T, K extends keyof T> = Omit<T, K> & Partial<Pick<T, K>>;

const useCategorySubmit = ({ getValues, reset }: UseFormReturn<Forms["Category"]>) => {
    const submit = useSubmit();

    const onSubmit = async () => {
        const values = getValues();
        const resetFormToCurrentValues = () => reset(values);

        const incomings: MakeOptional<
            CategoryView,
            "sum_tax_amount" | "verifications" | "has_attachments" | "id"
        >[] = values.incomings.map(item => {
            const cleaned = { ...item } as MakeOptional<
                CategoryView,
                "sum_tax_amount" | "verifications" | "has_attachments" | "id"
            >;
            delete cleaned.sum_tax_amount;
            delete cleaned.verifications;
            delete cleaned.has_attachments;
            delete cleaned.id;
            return cleaned;
        });
        const outgoings: MakeOptional<
            CategoryView,
            "sum_tax_amount" | "verifications" | "has_attachments" | "id"
        >[] = values.outgoings.map(item => {
            const cleaned = { ...item } as MakeOptional<
                CategoryView,
                "sum_tax_amount" | "verifications" | "has_attachments" | "id"
            >;
            delete cleaned.sum_tax_amount;
            delete cleaned.verifications;
            delete cleaned.has_attachments;
            delete cleaned.id;
            return cleaned;
        });

        try {
            await submit({ incomings, outgoings }, resetFormToCurrentValues);
        } catch (error) {
            const errorMessage = error instanceof Error ? error.message : String(error);
            log.error("Error saving categories:", errorMessage);
        }
    };

    return onSubmit;
};

const useCategoryFilter = (control: Control<Forms["Category"]>) => {
    const { fields: incomingsFields } = useFieldArray({
        control,
        name: "incomings",
        keyName: "fieldId",
    });
    const { fields: outgoingsFields } = useFieldArray({
        control,
        name: "outgoings",
        keyName: "fieldId",
    });

    const allCategoryDescriptions = useMemo(() => {
        return Array.from(
            new Set([
                ...outgoingsFields.map(field => field.description),
                ...incomingsFields.map(field => field.description),
            ]),
        );
    }, [incomingsFields, outgoingsFields]);

    const [selectedCategoryDescriptions, setSelectedCategoryDescriptions] = useState<string[]>([]);
    const [viewType, setViewType] = useState<ViewType>("all");
    const filteredIncomings = useMemo(() => {
        return incomingsFields
            .map((i, index) => ({ ...i, originalIndex: index }))
            .filter(
                field =>
                    selectedCategoryDescriptions.length === 0 ||
                    selectedCategoryDescriptions.includes(field.description),
            ) as FormField[];
    }, [selectedCategoryDescriptions, incomingsFields]);

    const filteredOutgoings = useMemo(() => {
        return outgoingsFields
            .map((i, index) => ({ ...i, originalIndex: index }))
            .filter(
                field =>
                    selectedCategoryDescriptions.length === 0 ||
                    selectedCategoryDescriptions.includes(field.description),
            ) as FormField[];
    }, [selectedCategoryDescriptions, outgoingsFields]);

    return {
        viewType,
        setViewType,
        selectedCategoryDescriptions,
        setSelectedCategoryDescriptions,
        filteredIncomings,
        filteredOutgoings,
        allCategoryDescriptions,
    };
};

const useDownloadCategoryAttachment = () => {
    const [isLoading, setIsLoading] = useState<(string | number)[]>([]);
    const { fetchAccountingCategoryAttachmentsPdf } = useQueryAccountingCategoryAttachmentsPdf();

    const onDownload = async (cid: string | number) => {
        try {
            setIsLoading(prev => [...prev, cid]);
            const data = await fetchAccountingCategoryAttachmentsPdf(cid);
            if (!data) return;

            const mimeType = data.blob.type || "";
            const extension = mimeType.split("/")[1] || "bin";
            const filename = data.filename ?? generateRandomFilename({ extension });
            const url = URL.createObjectURL(data.blob);

            triggerFileDownload({ url, filename });
            URL.revokeObjectURL(url);
        } catch (e) {
            log.error(e);
        } finally {
            setIsLoading(prev => prev.filter(id => id !== cid));
        }
    };

    return {
        isLoading,
        onDownload,
    };
};

export { useCategorySubmit, useCategoryFilter, useDownloadCategoryAttachment };
